/**
 * PriceTag Component
 */

import './styles.scss';

import { CurrencyFormat, CurrencySettings, formatMoney } from 'accounting';
import * as classNames from 'classnames';
import { extend } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';

import { CurrencyContext } from '../../../../contexts/currency';
import { ICurrencyContext } from '../../../../contexts/currency/types';
import Button from '../../buttons/Button';
import { IconWarning } from '../../images/Icon';
import Heading from '../Heading';
import { Span } from '../Label';

interface IOwnStateProps {
  language: string;
}

type ICurrencyContextProps = Pick<ICurrencyContext, 'currency'>;

interface IOwnProps extends ITextColorProps {
  amount: number;
  className?: string;
  currency?: GQL.Currency;
  rate?: number;
  explicitCurrency?: boolean;
  isDiscount?: boolean;
  label?: string;
  precision?: 0 | 2;
  size?: 'small' | 'medium' | 'large' | 'unset';
  strikeThrough?: boolean;
}

export type PriceTagOwnProps = IOwnProps;
type PriceTagProps = IOwnProps & IOwnStateProps & ICurrencyContextProps;

interface CurrencySymbol {
  [currencyCode: string]: {
    simple: string;
    explicit: string;
  };
}

const currencySymbol: CurrencySymbol = {
  CAD: {
    simple: '$',
    explicit: '$CA',
  },
  USD: {
    simple: '$',
    explicit: '$US',
  },
  EUR: {
    simple: '€',
    explicit: 'EUR',
  },
  GBP: {
    simple: '£',
    explicit: 'GBP',
  },
  AUD: {
    simple: '$',
    explicit: '$AU',
  },
  NZD: {
    simple: '$',
    explicit: '$NZ',
  },
};

const currencyFormat: { [locale: string]: CurrencySettings<CurrencyFormat> } = {
  fr: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: ' ',
  },
  // TODO: add the rest of the European currency formats
  de: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: '.',
  },
  es: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: '.',
  },
  it: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: '.',
  },
  nl: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: '.',
  },
  pt: {
    decimal: ',',
    format: {
      pos: '%v %s',
      neg: '-%v %s',
    },
    thousand: ' ',
  },
  en: {
    decimal: '.',
    format: {
      pos: '%s%v',
      neg: '-%s%v',
    },
    thousand: ',',
  },
};

function getCurrencyFormat(language: string) {
  const format = currencyFormat[language];
  return format ? format : currencyFormat.en;
}

const classes = new BEM('price-tag');

class PriceTag extends React.Component<PriceTagProps, {}> {
  static defaultProps: Partial<PriceTagProps> = {
    precision: 0,
  };

  render() {
    const {
      amount,
      className,
      currency,
      explicitCurrency,
      isDiscount,
      label,
      language,
      precision,
      rate,
      size,
      strikeThrough,
      textColor,
    } = this.props;

    if (!!!this.props.amount) {
      return (
        <Button
          variant="icon"
          textColor="bw7"
          icon={<IconWarning />}
          title="Amount could not be properly calculated."
        />
      );
    }

    const symbol = explicitCurrency ?
      language === 'en' ? `${currencySymbol[currency].explicit} ` : currencySymbol[currency].explicit
      : currencySymbol[currency].simple;

    const convertedAmount = rate ? rate * amount : amount;
    const formattedAmount = formatMoney(convertedAmount, extend({
      precision,
      symbol,
    }, getCurrencyFormat(language)));

    const price = `${label ? `${label} ` : ''}${formattedAmount}`;

    const classModifiers = {
      'line-through': !!strikeThrough,
      'discount': !!isDiscount,
    };
    const extraClasses = classNames(className);
    const commonProps = {
      textColor,
      ...classes('', classModifiers, extraClasses),
    };

    switch (size) {
      case 'small':
        return <Span size="medium" {...commonProps}>{price}</Span>;
      case 'medium':
        return <Heading is="h5" WrappedComponent="div" {...commonProps}>{price}</Heading>;
      case 'large':
        return <Heading is="h4" WrappedComponent="div" {...commonProps}>{price}</Heading>;
      default:
        return <Span {...commonProps}>{price}</Span>;
    }
  }
}

const ConnectedPriceTag = connect<IOwnStateProps, {}, IOwnProps & ICurrencyContextProps, IStoreState>(
  ({ preferences }) => ({
    language: preferences.language,
  }),
)(PriceTag);

const PriceTagWithContext: React.SFC<IOwnProps> = ({ currency, ...props }) => (
  <CurrencyContext.Consumer>
    {(ctx) => (
      <ConnectedPriceTag
        currency={currency || ctx.currency}
        {...props}
      />
    )}
  </CurrencyContext.Consumer>
);

export default PriceTagWithContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/atoms/text/PriceTag/index.tsx